import { Injectable } from '@angular/core';
import {Observable, Subject} from "rxjs";
import { deviceIds,Device } from 'src/embross-device-manager'
import {CussService} from "@services/cuss.service";
import {MessageSsbInt, SsbDataInt, SSBDDataTrasformInt} from "@app/interfaces/micro/ssbd.interface";
import {SsbdData} from "@models/micro/ssbd-data.model";
import { SsbdEmergencyService } from '@services/ssbd-emergency.service';
import { CussStatusCode } from '@enums/cuss-device-status.enum'
// ── CI22795_DIAG ── instrumentación temporal (CI-22795). Eliminar al cerrar la HU.
import { Ci22795DiagService } from '@services/ci22795-diag.service';

@Injectable({
  providedIn: 'root'
})
export class SbdService implements Device {
  device: Device;
  listen$: Subject<MessageSsbInt> = new Subject<MessageSsbInt>();
  timeout: number = 10000;
  isComplete: boolean = false;
  dictionary: SsbDataInt = {
    BS: "bagStatus", BT: "bagTagType", CW: "weightNunit", HT: "height", LT: "large", SW: "stableWeight", WT: "width",
    LP: "ticket", SD: "statusDevice"
  };

  constructor(
    private cussService: CussService,
    private ssbdEmergencyService: SsbdEmergencyService,
    // ── CI22795_DIAG ── instrumentación temporal (CI-22795). Eliminar al cerrar la HU.
    private ci22795Diag: Ci22795DiagService
  ) {
    if (cussService.getDeviceManager()){
      this.device = cussService.getDeviceManager().getDevice(deviceIds.AEA_BAGDROP);
    }
  }

  enable(): void {
    if (this.device) {
      // Reset emergency state for a clean transaction start
      this.ssbdEmergencyService.resetState();
      this.device.OnDeviceEvent = this.callback;
      this.device.enable();
    }
  }

  disable(): void {
    if (this.device) {
      // Restore the shared SSBD callback so emergency codes are still caught when idle
      this.device.OnDeviceEvent = this.cussService.getSharedSsbdCallback();
      this.device.disable();
    }
  }

  listen(): Observable<MessageSsbInt> {
    if(this.isComplete) {
      this.listen$ = new Subject<MessageSsbInt>();
      this.isComplete = false
    }
    return this.listen$.asObservable()
  }

  callback = (event: any) => {
    console.warn('[[[event-ssbd]]]', event)
    // ── CI22795_DIAG ── observa el evento crudo (incluidos `sendAEA` y `commandTimeout`,
    // que el switch de abajo no procesa). Solo actúa durante el drop-off. No altera el flujo.
    this.ci22795Diag.trackDeviceEvent(event, this.device);
    this.listen$.next(event);
    switch (event.key) {
      case 'unsolicitedMessage': {
        // Check for emergency codes first (139/302/305/122)
        this.ssbdEmergencyService.handleUnsolicitedMessage(event.value);
        const data = this.unsolicitedMessage(event.value);
        console.log('[[[data-ssbd]]]', data)
        // ── CI22795_DIAG ── evidencia AEA dentro de la ventana post-CC#RA. Solo observa
        // el objeto ya transformado; no lo muta ni altera la emisión de abajo.
        this.ci22795Diag.trackPostCcRaAeaState('unsolicitedMessage', 'getData', data, this.device);
        this.listen$.next({message:'getData', data})
        break;
      }
      case 'commandCompleted': {
        const dataComplete = this.commandComplete(event.value);
        // ── CI22795_DIAG ── ídem: permite saber si Embross emite `commandCompleted` tras CC#RA.
        this.ci22795Diag.trackPostCcRaAeaState(
          'commandCompleted',
          'Complete',
          dataComplete,
          this.device
        );
        this.listen$.next({message:'Complete', data: dataComplete})
        break;
      }
      case 'statusIsOK': // status, ok e.g: 302,0
      case 'statusChange': {
        // Route statusChange to the emergency service (counts 302, opens by 139/305)
        const statusCode = Array.isArray(event.value) ? event.value[0] : event.value;
        this.ssbdEmergencyService.handleStatusChange(statusCode);
        // Route statusOk
        const [value,...something] = event.key === 'statusIsOK' ? event.value.split(',') : event.value;
        const expectedErrorValue = this.expectedValue(value);
        this.listen$.next({message: expectedErrorValue, data: {} });
        this.ssbdEmergencyService.handleStatusIsOK(event.value);
        break;
      }
      default:
        break;
    }
  }
  isOK(): boolean {
    return this.device.isDeviceOk && this.device.deviceStatus != 305;
  }

  /** Status Query — Consulta el estado actual del dispositivo/banda */
  executeCommandSQ() {
    this.sendAEA('SQ', 3000);
  }
  /** Bag Query — Consulta datos de la maleta (peso, ticket, dimensiones). Inicia la lectura de la etiqueta y peso en la banda */
  executeCommandBQ() {
    this.sendAEA('BQ', 3000);
  }
  /** Detener/Pausar (acomodar maleta): CC#PA — Pausa la banda para posicionar la maleta */
  executeCommandCCP() {
    this.sendAEA('CC#PA', 30000);
  }
  /**
   * Adelante (enviar maleta): CC#RA — Activa la banda hacia adelante para enviar la maleta al sistema de equipaje
   *
   * CI-22795: devuelve si el comando fue REALMENTE invocado en el SDK.
   * @returns `true` si se ejecutó `device.sendAEA('CC#RA', …)`;
   *          `false` si el guard `isOK()` lo bloqueó (el dispositivo no estaba disponible).
   */
  executeCommandCCR(): boolean {
    return this.sendAEA('CC#RA', 30000);
  }
  /** Parada de emergencia: ES — Detiene la banda inmediatamente */
  executeCommandES() {
    this.sendAEA('ES', 10000);
  }

  /** Reset después de parar: RC — Resetea el conveyor después de una parada o al finalizar */
  executeCommandRC() {
    this.sendAEA('RC', 10000);
  }

  /** Posicionar la banda: EP{valor} — Mueve la banda a una posición específica (valor numérico) */
  executeCommandEP(value = 1000) {
    this.sendAEA(`EP${value}`, 10000);
  }

  getStatus() {
    this.device.status();
  }
  
  getIsOk() {
    this.device.isOK();
  }
  getStatusIsOk() {
    this.device.statusIsOK();
  }

  complete() {
    this.isComplete = true;
    this.listen$.complete();
  }
  /**
   * @returns CI-22795: `true` si el comando llegó realmente a `device.sendAEA()`;
   *          `false` si el guard `isOK()` lo descartó. Los `executeCommandX` que no
   *          necesitan ese dato simplemente ignoran el valor devuelto.
   */
  private sendAEA(command: string, time: number): boolean {
    // ── CI22795_DIAG ── El guard es funcionalmente idéntico al original: `isOK()` solo se
    // extrae a una constante para poder registrar exactamente el mismo valor que decide el
    // envío. `isOK()` sigue evaluándose una vez y en la misma posición (conserva su throw
    // si `device` fuese undefined). No se modifica la condición ni el comando enviado.
    const isOK = this.isOK();
    this.ci22795Diag.trackAeaAttempt(command, isOK, this.device);
    if (isOK) {
      this.device.sendAEA(command, time)
      // ── CI22795_DIAG ── la INVOCACIÓN al SDK ya se ejecutó. No prueba transmisión,
      // recepción por CUSS/SSBD ni arranque de la banda (ver Ci22795DiagService).
      this.ci22795Diag.trackAeaSdkInvoked(command, time);
      return true;
    }
    return false;
  }

  unsolicitedMessage(value: string): SSBDDataTrasformInt {
    return SsbdData.transform(value, this.dictionary);
  }

  private expectedValue(value: string) {
    let expected = '';
    switch(Number(value)) {
      case CussStatusCode.BAGGAGE_PRESENT:
        expected = "BAGGAGE_PRESENT"
      break;
      case CussStatusCode.BAGGAGE_ABSENT:
        expected = "BAGGAGE_ABSENT"
      break;
      case CussStatusCode.HARDWARE_ERROR:
        expected = "HARDWARE_ERROR"
      break;
      case CussStatusCode.MULTIPLE_BAGGAGE:
        expected = "MULTIPLE_BAGGAGE"
      break;
      default:
        expected = '';
      break;
    }
    return expected;
  }

  private commandComplete(value: string): SSBDDataTrasformInt {
    return SsbdData.transform(value, this.dictionary);
  }

}
